import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { Camera, MapPin, Heart, Edit, Upload, Sofa, Users, CheckCircle, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Profile = () => {
  const { user, token, refreshUser } = useContext(AuthContext);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    bio: '',
    profile_photo: '',
    interests: [],
    location: { city: '', country: '' },
    offers_couch_surfing: false,
    hosting_details: {
      space_type: '',
      max_guests: 1,
      amenities: [],
      house_rules: '',
      description: ''
    }
  });
  const [newInterest, setNewInterest] = useState('');
  const [newAmenity, setNewAmenity] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [subStatus, setSubStatus] = useState(null);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/payments/subscription`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setSubStatus(res.data)).catch(() => {});
    }
  }, [token]);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        bio: user.bio || '',
        profile_photo: user.profile_photo || '',
        interests: user.interests || [],
        location: user.location || { city: '', country: '' },
        offers_couch_surfing: user.offers_couch_surfing || false,
        hosting_details: user.hosting_details || {
          space_type: '',
          max_guests: 1,
          amenities: [],
          house_rules: '',
          description: ''
        }
      });
    }
  }, [user]);

  const handleSubmit = async () => {
    try {
      await axios.put(
        `${API}/users/update`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Profile updated successfully!');
      refreshUser();
      setEditing(false);
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const addInterest = () => {
    if (newInterest.trim() && !formData.interests.includes(newInterest.trim())) {
      setFormData({
        ...formData,
        interests: [...formData.interests, newInterest.trim()]
      });
      setNewInterest('');
    }
  };

  const removeInterest = (interest) => {
    setFormData({
      ...formData,
      interests: formData.interests.filter(i => i !== interest)
    });
  };

  const addAmenity = () => {
    if (newAmenity.trim() && !formData.hosting_details.amenities.includes(newAmenity.trim())) {
      setFormData({
        ...formData,
        hosting_details: {
          ...formData.hosting_details,
          amenities: [...formData.hosting_details.amenities, newAmenity.trim()]
        }
      });
      setNewAmenity('');
    }
  };

  const removeAmenity = (amenity) => {
    setFormData({
      ...formData,
      hosting_details: {
        ...formData.hosting_details,
        amenities: formData.hosting_details.amenities.filter(a => a !== amenity)
      }
    });
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type and size
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }
    
    setUploadingPhoto(true);
    const formDataUpload = new FormData();
    formDataUpload.append('files', file);
    
    try {
      // Upload to Cloudinary
      const uploadResponse = await axios.post(`${API}/upload/media`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (!uploadResponse.data || !uploadResponse.data.media || !uploadResponse.data.media[0]) {
        throw new Error('Invalid upload response');
      }
      
      // Extract URL from media object (backend returns {url, public_id, etc.})
      const mediaObject = uploadResponse.data.media[0];
      const photoUrl = typeof mediaObject === 'string' ? mediaObject : mediaObject.url;
      
      if (!photoUrl) {
        throw new Error('No URL in upload response');
      }
      
      // Immediately save to user profile - only send profile_photo field
      const updateResponse = await axios.put(
        `${API}/users/update`,
        { profile_photo: photoUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setFormData({
        ...formData,
        profile_photo: photoUrl
      });
      
      // Refresh user data to show new photo everywhere
      if (refreshUser) {
        refreshUser();
      }
      
      toast.success('Profile photo updated successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      
      // Handle different error formats
      let errorMessage = 'Failed to upload photo';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Check if detail is an array (validation errors)
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setUploadingPhoto(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-peach-50 to-teal-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 py-8 pt-24">
        <div className="glass-dark rounded-3xl p-8 shadow-2xl">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6 mb-8">
            <div className="relative">
              <Avatar className="w-32 h-32 border-4 border-teal shadow-xl">
                <AvatarImage src={user?.profile_photo} />
                <AvatarFallback className="bg-white text-black text-4xl font-bold">
                  {user?.name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              {/* Premium badge on avatar */}
              {user?.is_premium && (
                <div className="absolute -top-2 -right-2 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full p-2 shadow-lg">
                  <Crown className="w-5 h-5 text-white" />
                </div>
              )}
              {editing && (
                <button className="absolute bottom-0 right-0 bg-coral text-white p-2 rounded-full shadow-lg">
                  <Camera className="w-5 h-5" />
                </button>
              )}
            </div>

            <div className="flex-1 text-center sm:text-left">
              {/* Badges at the top */}
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mb-3">
                {user?.is_premium && (
                  <Badge data-testid="premium-badge" className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white font-semibold px-3 py-1">
                    <Crown className="w-3 h-3 mr-1" />
                    Premium{subStatus?.days_left != null ? ` · ${subStatus.days_left}d left` : ''}
                  </Badge>
                )}
                {user?.is_verified && (
                  <Badge className="bg-blue-500 text-white font-semibold px-3 py-1">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Verified
                  </Badge>
                )}
                {user?.offers_couch_surfing && (
                  <Badge className="bg-teal text-white font-semibold px-3 py-1">
                    <Sofa className="w-3 h-3 mr-1" />
                    Host
                  </Badge>
                )}
              </div>
              
              {editing ? (
                <Input
                  data-testid="name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="text-3xl font-bold mb-2 rounded-xl"
                />
              ) : (
                <h1 className="text-3xl sm:text-4xl font-bold mb-2">{user?.name}</h1>
              )}
              <p className="text-gray-600 mb-2">{user?.email}</p>
              {user?.location?.city && (
                <div className="flex items-center justify-center sm:justify-start space-x-1 text-gray-600">
                  <MapPin className="w-4 h-4" />
                  <span>{user.location.city}{user.location.country && `, ${user.location.country}`}</span>
                </div>
              )}
            </div>

            <Dialog open={editing} onOpenChange={setEditing}>
              <DialogTrigger asChild>
                <Button data-testid="edit-profile-btn" className="bg-white text-black border-2 border-teal hover:bg-teal hover:text-white rounded-full font-semibold">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Profile
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl bg-white rounded-3xl">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold">Edit Profile</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  {/* Profile Photo Upload */}
                  <div>
                    <label className="text-sm font-medium text-gray-700">Profile Photo</label>
                    <div className="flex items-center space-x-4 mt-2">
                      <Avatar className="w-20 h-20 border-4 border-teal">
                        <AvatarImage src={formData.profile_photo} />
                        <AvatarFallback className="bg-white text-black text-2xl font-bold">
                          {formData.name?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <input
                          type="file"
                          id="photo-upload"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          className="hidden"
                        />
                        <label
                          htmlFor="photo-upload"
                          className="cursor-pointer inline-flex items-center px-4 py-2 rounded-full bg-white text-black border-2 border-teal hover:bg-teal hover:text-white font-semibold text-sm"
                        >
                          <Camera className="w-4 h-4 mr-2" />
                          {uploadingPhoto ? 'Uploading...' : 'Change Photo'}
                        </label>
                        <p className="text-xs text-gray-500 mt-1">Max size: 5MB</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Name</label>
                    <Input
                      data-testid="edit-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1 rounded-xl"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Bio</label>
                    <Textarea
                      data-testid="edit-bio-input"
                      value={formData.bio}
                      onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                      className="mt-1 rounded-xl min-h-[100px]"
                      placeholder="Tell us about yourself..."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">City</label>
                      <Input
                        data-testid="edit-city-input"
                        value={formData.location?.city || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          location: { ...formData.location, city: e.target.value }
                        })}
                        className="mt-1 rounded-xl"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Country</label>
                      <Input
                        data-testid="edit-country-input"
                        value={formData.location?.country || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          location: { ...formData.location, country: e.target.value }
                        })}
                        className="mt-1 rounded-xl"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Interests</label>
                    <div className="flex space-x-2 mt-1">
                      <Input
                        data-testid="add-interest-input"
                        value={newInterest}
                        onChange={(e) => setNewInterest(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addInterest()}
                        className="rounded-xl"
                        placeholder="Add interest..."
                      />
                      <Button
                        data-testid="add-interest-btn"
                        onClick={addInterest}
                        className="bg-teal text-white rounded-xl"
                      >
                        Add
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {formData.interests.map((interest) => (
                        <Badge
                          key={`interest-${interest}`}
                          className="bg-coral text-white cursor-pointer"
                          onClick={() => removeInterest(interest)}
                        >
                          {interest} ×
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Couch Surfing Section - Premium Only */}
                  {user?.is_premium ? (
                    <div className="border-t pt-4 mt-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                          <Sofa className="w-5 h-5 text-teal" />
                          <h3 className="text-lg font-bold">Couch Surfing</h3>
                          <Badge className="bg-gold text-white">Premium</Badge>
                        </div>
                        <Switch
                          checked={formData.offers_couch_surfing}
                          onCheckedChange={(checked) => setFormData({
                            ...formData,
                            offers_couch_surfing: checked
                          })}
                        />
                      </div>

                      {formData.offers_couch_surfing && (
                        <div className="space-y-3 bg-gray-50 p-4 rounded-xl">
                          <div>
                            <Label>Space Type</Label>
                            <Select
                              value={formData.hosting_details.space_type}
                              onValueChange={(value) => setFormData({
                                ...formData,
                                hosting_details: { ...formData.hosting_details, space_type: value }
                              })}
                            >
                              <SelectTrigger className="rounded-xl">
                                <SelectValue placeholder="Select space type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="couch">Couch</SelectItem>
                                <SelectItem value="private_room">Private Room</SelectItem>
                                <SelectItem value="shared_room">Shared Room</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label>Max Guests</Label>
                            <Input
                              type="number"
                              min="1"
                              max="10"
                              value={formData.hosting_details.max_guests}
                              onChange={(e) => setFormData({
                                ...formData,
                                hosting_details: { ...formData.hosting_details, max_guests: parseInt(e.target.value) || 1 }
                              })}
                              className="rounded-xl"
                            />
                          </div>

                          <div>
                            <Label>Description</Label>
                            <Textarea
                              value={formData.hosting_details.description}
                              onChange={(e) => setFormData({
                                ...formData,
                                hosting_details: { ...formData.hosting_details, description: e.target.value }
                              })}
                              placeholder="Describe your space..."
                              className="rounded-xl"
                              rows={3}
                            />
                          </div>

                          <div>
                            <Label>Amenities</Label>
                            <div className="flex space-x-2">
                              <Input
                                value={newAmenity}
                                onChange={(e) => setNewAmenity(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAmenity())}
                                placeholder="WiFi, Kitchen, etc."
                                className="rounded-xl"
                              />
                              <Button onClick={addAmenity} className="bg-teal text-white rounded-xl">
                                Add
                              </Button>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {formData.hosting_details.amenities.map((amenity) => (
                                <Badge
                                  key={`amenity-${amenity}`}
                                  className="bg-teal text-white cursor-pointer"
                                  onClick={() => removeAmenity(amenity)}
                                >
                                  {amenity} ×
                                </Badge>
                              ))}
                            </div>
                          </div>

                          <div>
                            <Label>House Rules</Label>
                            <Textarea
                              value={formData.hosting_details.house_rules}
                              onChange={(e) => setFormData({
                                ...formData,
                                hosting_details: { ...formData.hosting_details, house_rules: e.target.value }
                              })}
                              placeholder="No smoking, quiet hours, etc."
                              className="rounded-xl"
                              rows={2}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="border-t pt-4 mt-4">
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center space-x-2">
                          <Sofa className="w-5 h-5 text-gray-400" />
                          <span className="text-gray-600">Couch Surfing</span>
                        </div>
                        <Badge className="bg-gold text-white">Premium Only</Badge>
                      </div>
                    </div>
                  )}

                  <Button
                    data-testid="save-profile-btn"
                    onClick={handleSubmit}
                    className="w-full bg-gradient-to-r from-coral to-peach text-white rounded-xl py-6"
                  >
                    Save Changes
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Bio Section */}
          {user?.bio && (
            <div className="mb-6">
              <h2 className="text-xl font-bold mb-2">About Me</h2>
              <p className="text-gray-700 whitespace-pre-wrap">{user.bio}</p>
            </div>
          )}

          {/* Interests */}
          {user?.interests && user.interests.length > 0 && (
            <div className="mb-6">
              <h2 className="text-xl font-bold mb-3">Interests</h2>
              <div className="flex flex-wrap gap-2">
                {user.interests.map((interest, idx) => (
                  <Badge key={`interest-view-${interest || idx}`} className="bg-gradient-to-r from-peach to-gold text-gray-900 font-semibold border-none px-4 py-2">
                    <Heart className="w-3 h-3 mr-1" />
                    {interest}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Couch Surfing Info */}
          {user?.is_premium && user?.offers_couch_surfing && user?.hosting_details && (
            <div className="mb-6">
              <div className="flex items-center space-x-2 mb-3">
                <Sofa className="w-6 h-6 text-teal" />
                <h2 className="text-xl font-bold">Offers Couch Surfing</h2>
                <Badge className="bg-teal text-white">Host Available</Badge>
              </div>
              <div className="glass-dark rounded-2xl p-4 space-y-3">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-teal" />
                  <span className="font-semibold capitalize">{user.hosting_details.space_type?.replace('_', ' ')}</span>
                  <span className="text-gray-600">• Up to {user.hosting_details.max_guests} guests</span>
                </div>
                
                {user.hosting_details.description && (
                  <p className="text-gray-700">{user.hosting_details.description}</p>
                )}
                
                {user.hosting_details.amenities && user.hosting_details.amenities.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-gray-600 mb-1">Amenities:</p>
                    <div className="flex flex-wrap gap-2">
                      {user.hosting_details.amenities.map((amenity) => (
                        <Badge key={`amenity-view-${amenity}`} className="bg-white text-teal border border-teal">
                          {amenity}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {user.hosting_details.house_rules && (
                  <div>
                    <p className="text-sm font-semibold text-gray-600 mb-1">House Rules:</p>
                    <p className="text-sm text-gray-700">{user.hosting_details.house_rules}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mt-8 pt-8 border-t border-gray-200">
            <div className="text-center">
              <div className="text-3xl font-bold text-coral">0</div>
              <div className="text-gray-600 text-sm">Posts</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-teal">0</div>
              <div className="text-gray-600 text-sm">Connections</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-gold">0</div>
              <div className="text-gray-600 text-sm">Messages</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;